import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

interface BreadcrumbContextValue {
  labels: Record<string, string>;
  setLabel: (path: string, label?: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  labels: {},
  setLabel: () => undefined,
});

export const BreadcrumbProvider = ({ children }: { children: React.ReactNode }) => {
  const [labels, setLabels] = useState<Record<string, string>>({});

  const setLabel = useCallback((path: string, label?: string) => {
    setLabels((prev) => {
      if (label === undefined) {
        if (!(path in prev)) return prev;
        const { [path]: _removed, ...rest } = prev;
        return rest;
      }
      return prev[path] === label ? prev : { ...prev, [path]: label };
    });
  }, []);

  const value = useMemo(() => ({ labels, setLabel }), [labels, setLabel]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

export const useBreadcrumbLabels = () => useContext(BreadcrumbContext).labels;

/**
 * Names a breadcrumb of the current page (e.g. the entity name on a detail page).
 * By default it names the last crumb; pass `trailingSegments` to name an ancestor instead,
 * e.g. 1 for `/packages/5/edit` to name the `5` crumb.
 * Falls back to the generic label until `label` is available.
 */
export const useBreadcrumbLabel = (label?: string, trailingSegments = 0) => {
  const { pathname } = useLocation();
  const { setLabel } = useContext(BreadcrumbContext);

  useEffect(() => {
    if (!label) return;
    const segments = pathname.split("/").filter(Boolean);
    const path = `/${segments.slice(0, segments.length - trailingSegments).join("/")}`;
    setLabel(path, label);
    return () => setLabel(path);
  }, [pathname, label, trailingSegments, setLabel]);
};
